import { useRef, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'sonner'
import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Save, Loader2 } from 'lucide-react'
import { ContentBlocksManager } from '@/containers/ContentBlocks/ContentBlocksManager'
import { ContentBlocksManagerHandle } from '@/containers/ContentBlocks/types'
import {
  GET_HOMEPAGE_SETTINGS,
  UPDATE_SHOP_SETTING_CONTENT_BLOCKS,
} from './homePageSettingsQueries'

export default function HomePageSettings() {
  const { data, loading } = useQuery(GET_HOMEPAGE_SETTINGS)
  const [updateShopSetting] = useMutation(UPDATE_SHOP_SETTING_CONTENT_BLOCKS)
  const managerRef = useRef<ContentBlocksManagerHandle>(null)
  const [saving, setSaving] = useState(false)

  const shopSetting = data?.findFirstShopSetting
  const initialBlocks = shopSetting?.homepage_content_blocks ?? []

  const handleSave = async () => {
    if (!shopSetting || !managerRef.current) return

    setSaving(true)
    try {
      await updateShopSetting({
        variables: {
          where: { id: shopSetting.id },
          data: {
            homepage_content_blocks: {
              set: managerRef.current.getBlockIds(),
            },
          },
        },
      })

      await managerRef.current.commitArrangement()

      toast.success('Homepage content blocks updated successfully')
    } catch (error) {
      console.error('Save error:', error)
      toast.error('Failed to update homepage content blocks')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <div className="flex justify-between items-center mb-8">
        <HeaderSection
          title="Home Page Settings"
          subtitle="Manage content blocks displayed on the homepage"
        />
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save
        </Button>
      </div>

      <Card className="p-6">
        <ContentBlocksManager
          ref={managerRef}
          owner={{ kind: 'shop', id: shopSetting?.id }}
          initialBlocks={initialBlocks}
          enableInlineCreate={false}
          enablePermanentDelete={false}
          emptyText='No content blocks assigned. Click "Add Content Block" to get started.'
        />
      </Card>
    </PageContent>
  )
}
